import {
  Box,
  Button,
  Drawer,
  Flex,
  Grid,
  MultiSelect,
  Select,
  Text,
} from "@mantine/core";
import { GoChevronDown } from "react-icons/go";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import DateRangePicker from "./DateRangePicker";
import useStyles from "./style";
import { useSelector } from "react-redux";
import { https } from "../../http";

const NoteReportDrawer = ({ drawerOpen, drawerClose }) => {
  const { classes, cx } = useStyles();
  const [customError, setCustomError] = useState({});
    const { allPatients } = useSelector((state) => state?.patient);

  const [pdfGenerating, setPdfGenerating] = useState(false);
  const { allFacilities } = useSelector((state) => state?.facility);
  const { allNotesTypes } = useSelector((state) => state?.note);

  const form = useForm({
    initialValues: {
      client: "",
      facility: "",
      noteTypes: [],
      shift: "",
    },
    validate: {
      client: (value) => (value ? null : "Required"),
      facility: (value) => (value ? null : "Required"),
      noteTypes: (value) => (value?.length > 0 ? null : "Required"),
    },
  });
  const [dateRange, setDateRange] = useState({
    type: null,
    date: [],
  });
  const [period, setPeriod] = useState([]);
  const [opened, { open, close }] = useDisclosure(false);

  //   const isFormValid =
  //   form.values.client.length > 0 &&
  //   form.values.facility &&
  //   form.values.noteTypes.length > 0 &&
  //  period[0] || period[1]

  const isFormValid =
    form.values.client.length > 0 &&
    form.values.facility &&
    form.values.noteTypes.length > 0 &&
    period[0] &&
    period[1];

  const facilityName =
    allFacilities?.facilities?.find(
      (facility) => facility.id === form.values.facility,
    )?.name || "";
  const handleApply = () => {
    if (dateRange.date?.[0] && dateRange.date?.[1]) {
      setPeriod(dateRange.date);
      setCustomError((prev) => ({ ...prev, period: "" }));
      close();
    }
  };
  const handleCancel = () => {
    setPeriod([]);
    form.reset();
    setCustomError((prev) => ({ ...prev, period: "" }));
    setDateRange({
      type: null,
      date: [],
    });

    drawerClose();
  };

  const handleSubmit = async (values) => {
    const errors = {};
    if (!period[0] || !period[1]) errors.period = "Required";
    if (Object.keys(errors).length) {
      setCustomError(errors);
      return;
    }

    setPdfGenerating(true);
    try {
      // Single call to the backend — it fetches, generates, and streams the PDF.
      // responseType: "blob" tells axios to treat the response as binary data.
      const response = await https().post(
        "/note/generatePdfReport",
        {
          facilityId: values?.facility,
          clientId: values?.client,
          noteTypes: values?.noteTypes,
          shift: values?.shift || null,
          startDate: period[0].toISOString(),
          endDate: period[1].toISOString(),
          facilityName,
          reportPeriod: `${period[0].toLocaleDateString()} - ${period[1].toLocaleDateString()}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        { responseType: "blob" },
      );

      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Note_Report_${facilityName}.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      handleCancel();
    } catch (error) {
      console.error("Note report error:", error);
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <Drawer
      opened={drawerOpen}
      offset={8}
      size="55%"
      sx={{
        ".mantine-Drawer-close": {
          color: "#000",
          "& svg": {
            width: "25px !important",
            height: "25px !important",
          },
        },
        ".mantine-11shxvp": {
          background: "#F5F5F5",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
        },
        ".mantine-Drawer-close:hover": {
          color: "#FFAF4A",
        },
        ".mantine-1q36a81": {
          padding: "0",
        },
      }}
      zIndex={1000}
      title={
        <Text fz={24} fw={700} ff="Inter" color="#000">
          Create Report
        </Text>
      }
      position="right"
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
      shadow="md"
      onClose={handleCancel}
      centered
    >
      <Flex direction="column" py={14} px={24}>
        <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
          <Box className={classes.formBox}>
            <Grid gutter={24}>
              <Grid.Col span={6}>
                <Select
                  size="sm"
                  className={classes.inputField}
                  searchable
                  withAsterisk
                  label="Facility"
                  placeholder="Select Facility"
                  rightSectionPointerEvents="none"
                  rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                  {...form.getInputProps("facility")}
                  onChange={(val) => {
                    form.setFieldValue("client", "");
                    form.setFieldValue("facility", val);
                  }}
                  data={
                    allFacilities && allFacilities?.facilities
                      ? allFacilities?.facilities
                          ?.filter(({ active, deleted }) => active && !deleted)
                          ?.map((opt) => ({
                            value: opt?.id,
                            label: opt?.name,
                          }))
                      : []
                  }
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <MultiSelect
                  searchable
                  label="Client"
                  disabled={!form.values.facility}
                  multiple
                  withAsterisk
                  size="sm"
                  className={cx(classes.inputField, classes.multiSelect)}
                  rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                  placeholder="Select Client"
                  rightSectionPointerEvents="none"
                  data={[
                    { value: "ALL", label: "All Clients" }, // 👈 Add this at the top
                    ...(allPatients
                      ? allPatients
                          ?.filter(
                            (p) =>
                              p?.active === true &&
                              p?.deleted === false &&
                              p?.facilityId?.some(
                                (f) =>
                                  f?.id === form.values.facility &&
                                  f?.active === true &&
                                  f?.deleted === false,
                              ),
                          )
                          ?.map((opt) => ({
                            value: opt?.id,
                            label: opt?.name,
                          }))
                      : []),
                  ]}
                  value={form.values.client}
                  onChange={(val) => {
                    if (val.includes("ALL")) {
                      // get all clients for this facility
                      const facilityClients = allPatients
                        ?.filter(
                          (p) =>
                            p?.active === true &&
                            p?.deleted === false &&
                            p?.facilityId?.some(
                              (f) =>
                                f?.id === form.values.facility &&
                                f?.active === true &&
                                f?.deleted === false,
                            ),
                        )
                        ?.map((opt) => opt?.id);

                      // set all client ids
                      form.setFieldValue("client", facilityClients);
                    } else {
                      form.setFieldValue("client", val);
                    }
                  }}
                  error={form.errors.client}
                />

                {/* <MultiSelect
                  searchable
                  label="Client"
                  disabled={!form.values.facility}
                  multiple
                  size="sm"
                  className={cx(classes.inputField, classes.multiSelect)}
                  rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                  placeholder="Select Client"
                  rightSectionPointerEvents="none"
                  data={
                    allPatients
                      ? allPatients
                          ?.filter(
                            (p) =>
                              p?.active === true &&
                              p?.deleted === false &&
                              p?.facilityId?.some(
                                (f) =>
                                  f?.id === form.values.facility &&
                                  f?.active === true &&
                                  f?.deleted === false
                              )
                          )
                          ?.map((opt) => ({
                            value: opt?.id,
                            label: opt?.name,
                          }))
                      : []
                  }
                  {...form.getInputProps("client")}
                /> */}
              </Grid.Col>
              <Grid.Col span={6}>
                <MultiSelect
                  searchable
                  label="Note Type"
                  multiple
                  withAsterisk
                  size="sm"
                  className={cx(classes.inputField, classes.multiSelect)}
                  rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                  placeholder="Select Note Type"
                  rightSectionPointerEvents="none"
                  data={[
                    { value: "ALL", label: "All Note Types" }, // 👈 Add this at the top
                    ...(allNotesTypes?.ntypes
                      ? allNotesTypes?.ntypes
                          ?.filter(
                            (opt) =>
                              opt?.name?.toLowerCase() !== "billing services", // 🚫 exclude billing
                          )
                          ?.map((opt) => ({
                            value: opt?.id,
                            label: opt?.name,
                          }))
                      : []),
                  ]}
                  value={form.values.noteTypes}
                  onChange={(val) => {
                    if (val.includes("ALL")) {
                      const allTypes =
                        allNotesTypes?.ntypes?.map((opt) => opt?.id) || [];
                      form.setFieldValue("noteTypes", allTypes); // ✅ select all note types
                    } else {
                      form.setFieldValue("noteTypes", val);
                    }
                  }}
                  error={form.errors.noteTypes}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                {/* <Text fz={14} fw={500} ff="Inter" color="#262626">
                  Period
                </Text> */}
                <Text fz={14} fw={500} ff="Inter" color="#262626">
                  Period <span style={{ color: "red" }}>*</span>
                </Text>

                <DateRangePicker
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  handleApply={handleApply}
                  open={open}
                  opened={opened}
                  close={close}
                  size="md"
                  mt={20}
                  style={{ marginTop: "20px" }}
                />
                {customError.period && (
                  <Text color="red" fz={16} mt={4}>
                    {customError.period}
                  </Text>
                )}
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  size="sm"
                  className={classes.inputField}
                  label="Shift"
                  placeholder="Select Shift"
                  rightSectionPointerEvents="none"
                  rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                  clearable
                  data={[
                    { value: "all", label: "All" },
                    { value: "morning", label: "Morning" },
                    { value: "evening", label: "Evening" },
                    { value: "night", label: "Night" },
                  ]}
                  {...form.getInputProps("shift")}
                />
              </Grid.Col>
            </Grid>
          </Box>
          <Flex gap={20} align="center" justify="flex-end">
            <Button
              className={classes.cancelBtn}
              onClick={() => handleCancel()}
            >
              Cancel
            </Button>
            <Button
              disabled={!isFormValid || pdfGenerating}
              type="submit"
              className={classes.createBtn}
              loading={pdfGenerating}
            >
              {pdfGenerating ? "Generating PDF..." : "Create report"}
            </Button>
          </Flex>
        </form>
      </Flex>
    </Drawer>
  );
};

export default NoteReportDrawer;
